import { useCallback, useEffect, useState } from "react";
import { Clock, ExternalLink, Gift, Pencil, Plus, Trash2, Truck, X } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fieldErrs } from "@/lib/api";
import { money } from "@/lib/format";
import { useHub } from "@/components/AppShell";
import { EmptyState, Field, Loader, PageHeader } from "@/components/common";
import { Confirm, FormDialog, KIDS_RGB, KidTabs, ProgressBar, useBusy } from "@/components/kids/kidsUi";
import { OrderLinks } from "@/components/kids/Approvals";
import { useAffordCelebrations } from "@/components/piggy/PiggyStage";
import { useLiveRefresh } from "@/lib/live";

function WishForm({ item, kidId, hubId, onClose, onSaved }) {
  const [f, setF] = useState({ name: item?.name || "", price: item ? String(item.price) : "", link: item?.link || "" });
  const [errors, setErrors] = useState({});
  const save = async () => {
    const errs = {};
    if (!f.name.trim()) errs.name = "Add a name";
    const p = Number(f.price);
    if (!(p >= 0.01 && p <= 10000)) errs.price = "Price must be between $0.01 and $10,000";
    setErrors(errs);
    if (Object.keys(errs).length) return;
    try {
      if (item) await api.put(`/organizations/${hubId}/wish-items/${item.id}`, { ...f, price: p });
      else await api.post(`/organizations/${hubId}/wish-lists/${kidId}/items`, { ...f, price: p });
      toast.success(item ? "Wish updated" : "Added to your wish list");
      onSaved();
    } catch (e) { setErrors(fieldErrs(e) || { form: errMsg(e) }); }
  };
  return (
    <FormDialog open onClose={onClose} title={item ? "Edit wish" : "Add a wish"} testid="wish-form">
      <div className="space-y-4">
        <Field label="Name" htmlFor="wish-name" error={errors.name} errorTestid="wish-name-error">
          <input id="wish-name" className="q-input" maxLength={100} value={f.name} aria-invalid={!!errors.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="wish-name-input" />
        </Field>
        <Field label="Price ($)" htmlFor="wish-price" error={errors.price} errorTestid="wish-price-error">
          <input id="wish-price" type="number" step="0.01" min="0" className="q-input" value={f.price} aria-invalid={!!errors.price} onChange={(e) => setF({ ...f, price: e.target.value })} data-testid="wish-price-input" />
        </Field>
        <Field label="Link (optional)" htmlFor="wish-link" error={errors.link} errorTestid="wish-link-error">
          <input id="wish-link" className="q-input" placeholder="https://www.amazon.com/..." value={f.link} onChange={(e) => setF({ ...f, link: e.target.value })} data-testid="wish-link-input" />
        </Field>
        {errors.form && <p className="q-error" data-testid="wish-form-error">{errors.form}</p>}
        <div className="flex justify-end gap-2">
          <button className="q-btn q-btn-ghost" onClick={onClose} data-testid="wish-cancel-btn">Cancel</button>
          <button className="q-btn q-btn-primary min-h-[44px]" onClick={save} data-testid="wish-save-btn">Save wish</button>
        </div>
      </div>
    </FormDialog>
  );
}

function LinkBox({ list, hubId, onSaved }) {
  const [url, setUrl] = useState(list.wishlist_url || "");
  const [error, setError] = useState("");
  const save = async () => {
    setError("");
    try { await api.put(`/organizations/${hubId}/wish-lists/${list.kid.id}/link`, { url }); toast.success("Wish list link saved"); onSaved(); }
    catch (e) { setError(errMsg(e)); }
  };
  return (
    <div className="q-card mb-6 space-y-3" data-testid="wishlist-link-box">
      {list.wishlist_url && (
        <a className="q-btn min-h-[48px] justify-center text-base font-extrabold text-white" style={{ background: `rgb(${KIDS_RGB})` }} href={list.wishlist_url} target="_blank" rel="noreferrer" data-testid="open-amazon-wishlist-btn">
          <ExternalLink className="h-5 w-5" /> Open my Amazon wish list
        </a>
      )}
      {list.can_edit && (
        <div>
          <label className="q-label" htmlFor="wl-url">Amazon wish list link</label>
          <div className="flex flex-col gap-2 sm:flex-row">
            <input id="wl-url" className="q-input" placeholder="https://www.amazon.com/hz/wishlist/ls/..." value={url} aria-invalid={!!error} onChange={(e) => setUrl(e.target.value)} data-testid="wishlist-link-input" />
            <button className="q-btn q-btn-secondary min-h-[44px] justify-center" onClick={save} data-testid="wishlist-link-save-btn">Save link</button>
          </div>
          {error && <p className="q-error" data-testid="wishlist-link-error">{error}</p>}
        </div>
      )}
    </div>
  );
}

function WishCard({ w, list, hubId, onChanged, onEdit, onDelete }) {
  const [busy, run] = useBusy();
  const act = (path, msg) => run(path, async () => {
    try { await api.post(`/organizations/${hubId}/wish-items/${w.id}/${path}`); toast.success(msg); onChanged(); } catch (e) { toast.error(errMsg(e)); }
  });
  const kidView = list.is_kid;
  const open = w.status === "none";
  return (
    <li className="q-card space-y-3" data-testid={`wish-${w.id}`} data-status={w.status}>
      <div className="flex items-start gap-3">
        <span className="inline-flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-xl" style={{ background: `rgba(${KIDS_RGB},0.12)` }}><Gift className="h-6 w-6" style={{ color: `rgb(${KIDS_RGB})` }} /></span>
        <div className="min-w-0 flex-1">
          <p className="font-display text-lg font-extrabold text-q-text" data-testid={`wish-name-${w.id}`}>{w.name}</p>
          <p className="text-base font-bold text-q-text">{money(w.final_price ?? w.price)}</p>
          {w.link && <a className="text-xs font-semibold text-q-accent underline-offset-2 hover:underline" href={w.link} target="_blank" rel="noreferrer" data-testid={`wish-link-${w.id}`}>View item</a>}
        </div>
        {list.can_edit && open && (
          <div className="flex gap-0.5">
            <button className="q-btn q-btn-ghost h-11 w-11 justify-center p-0" onClick={() => onEdit(w)} aria-label="Edit wish" data-testid={`wish-edit-btn-${w.id}`}><Pencil className="h-4 w-4" /></button>
            <button className="q-btn q-btn-ghost h-11 w-11 justify-center p-0 hover:text-red-300" onClick={() => onDelete(w)} aria-label="Delete wish" data-testid={`wish-delete-btn-${w.id}`}><Trash2 className="h-4 w-4" /></button>
          </div>
        )}
      </div>
      {w.reserved && <p className="mt-2 text-sm font-semibold" style={{ color: "rgb(var(--glow-kids))" }} data-testid={`wish-reserved-${w.id}`}>{w.reserved_note}</p>}
      {(open || w.status === "requested") && (
        <>
          <ProgressBar percent={w.progress} testid={`wish-progress-${w.id}`} />
          <p className="text-sm font-bold" style={{ color: w.can_afford ? "rgb(var(--glow-money))" : "rgb(var(--q-muted))" }} data-testid={`wish-afford-${w.id}`}>
            {w.can_afford ? "You can afford this!" : `${money(w.to_go)} to go`}
          </p>
        </>
      )}
      {w.decline_note && open && <p className="text-sm font-semibold text-q-accent" data-testid={`wish-declined-${w.id}`}>Not this time: {w.decline_note}</p>}
      {open && kidView && (
        <button className="q-btn min-h-[48px] w-full justify-center text-base font-extrabold text-white disabled:opacity-60" style={{ background: w.can_afford ? `rgb(${KIDS_RGB})` : "rgb(var(--q-border-strong))" }}
          disabled={!w.can_afford || !!busy} onClick={() => act("request", "Asked a grown-up!")} data-testid={`wish-want-btn-${w.id}`}>
          {w.can_afford ? "I want this" : `${money(w.to_go)} to go`}
        </button>
      )}
      {w.status === "requested" && (
        <div className="flex flex-wrap items-center gap-2">
          <span className="inline-flex items-center gap-1.5 text-sm font-bold text-q-text" data-testid={`wish-waiting-${w.id}`}><Clock className="h-4 w-4 text-q-accent" /> Waiting for a grown-up</span>
          {list.can_edit && kidView && <button className="q-btn q-btn-secondary min-h-[44px]" onClick={() => act("cancel", "Request cancelled")} data-testid={`wish-cancel-request-btn-${w.id}`}><X className="h-4 w-4" /> Cancel request</button>}
        </div>
      )}
      {w.status === "approved" && (kidView ? <p className="text-sm font-bold" style={{ color: "rgb(var(--glow-money))" }} data-testid={`wish-approved-${w.id}`}>Approved! A grown-up will order it.</p>
        : list.can_approve && <OrderLinks item={w} hubId={hubId} onDone={onChanged} />)}
      {w.status === "ordered" && <p className="inline-flex items-center gap-1.5 text-sm font-bold" style={{ color: "rgb(var(--glow-money))" }} data-testid={`wish-on-its-way-${w.id}`}><Truck className="h-4 w-4" /> On its way!</p>}
    </li>
  );
}

function WishCelebrations({ hubId, kidId }) {
  const [list, setList] = useState(null);
  useEffect(() => { api.get(`/organizations/${hubId}/piggy-bank/${kidId}`).then(({ data }) => setList(data.celebrations)).catch(() => {}); }, [hubId, kidId]);
  return useAffordCelebrations(hubId, kidId, list);
}

export default function WishList() {
  const { hub, reloadHub } = useHub();
  const [data, setData] = useState(null);
  const [kidId, setKidId] = useState(null);
  const [form, setForm] = useState(null);
  const [del, setDel] = useState(null);
  const load = useCallback(() => api.get(`/organizations/${hub.id}/wish-lists`).then(({ data: d }) => { setData(d); setKidId((k) => k || d.kids[0]?.kid.id); }), [hub.id]);
  useEffect(() => { load(); }, [load]);
  useLiveRefresh(() => load(), (m) => ["wish", "piggy.ledger", "chore"].includes(m.entity), null);
  if (!data) return <Loader />;
  const list = data.kids.find((k) => k.kid.id === kidId);
  const kidSelf = hub.my_role === "kid" && list;
  const changed = () => { load(); reloadHub(); };
  const full = list && list.items.length >= 50;
  const remove = async () => {
    try { await api.delete(`/organizations/${hub.id}/wish-items/${del.id}`); toast.success("Wish removed"); setDel(null); changed(); } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div data-testid="wish-page">
      <PageHeader eyebrow="Kids Corner" title={hub.my_role === "kid" ? "My wish list" : "Wish lists"} rgb={KIDS_RGB} testid="wish-title"
        actions={list?.can_edit && <button className="q-btn q-btn-primary min-h-[44px]" onClick={() => (full ? toast.error("Your wish list is full (50 items)") : setForm({}))} data-testid="add-wish-btn"><Plus className="h-4 w-4" /> Add a wish</button>} />
      {hub.my_role !== "kid" && <KidTabs kids={data.kids.map((k) => k.kid)} value={kidId} onChange={setKidId} />}
      {list && (
        <>
          <p className="mb-4 text-base font-semibold text-q-muted q-onwall" data-testid="wish-balance">{hub.my_role === "kid" ? "You have" : `${list.kid.name} has`} {money(list.balance)} in the piggy bank</p>
          {full && <p className="mb-4 text-sm font-semibold text-q-accent" data-testid="wish-full-msg">Your wish list is full (50 items)</p>}
          <LinkBox key={list.kid.id + (list.wishlist_url || "")} list={list} hubId={hub.id} onSaved={load} />
          {list.items.length === 0 ? <EmptyState Icon={Gift} title="No wishes yet" hint="Add something you're saving up for." rgb={KIDS_RGB} testid="wish-empty" /> : (
            <ul className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
              {list.items.map((w) => <WishCard key={w.id} w={w} list={list} hubId={hub.id} onChanged={changed} onEdit={(x) => setForm({ item: x })} onDelete={setDel} />)}
            </ul>
          )}
        </>
      )}
      {form && <WishForm item={form.item} kidId={list.kid.id} hubId={hub.id} onClose={() => setForm(null)} onSaved={() => { setForm(null); changed(); }} />}
      {kidSelf && <WishCelebrations hubId={hub.id} kidId={list.kid.id} />}
      <Confirm open={!!del} onClose={() => setDel(null)} title="Remove this wish?" body={del?.name} onConfirm={remove} testid="wish-delete-confirm" />
    </div>
  );
}
